// v0.4.89 — Tiny click-to-popover info circle. Pattern signed
// off 2026-06-20: country-return surfaces (box cards, banner
// fix lines, technical-details disclosures) were dense walls of
// text. Operators want the bold label + value first; long-form
// rationale, formulas, statutory restatements, and engineer-
// jargon should sit behind a clickable "ⓘ" they can summon when
// needed and ignore otherwise.
//
// Usage:
//   <InfoCircle testid="box-info-01">
//     {/* i18n-ignore */}<p>Long explanation here.</p>
//     {/* i18n-ignore */}<p>Multiple paragraphs OK.</p>
//   </InfoCircle>
//
// The popover positions below the icon by default and closes on
// outside click. Width capped at 320px so it never blows out
// narrow card layouts.

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";


export const InfoCircle = ({
  testid = "info-circle",
  children,
  // Optional accessible label override; defaults to "More info".
  ariaLabel,
  // align="left" | "right" — which edge of the icon the popover
  // anchors to. Defaults to left.
  align = "left",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        type="button"
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={ariaLabel || t("ui.common.more_info", { defaultValue: "More info" })}
        aria-expanded={open ? "true" : "false"}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] tabular-nums leading-none border transition-colors ${
          open
            ? "bg-teal-600 text-white border-teal-600"
            : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:text-teal-700 dark:hover:text-teal-300 hover:border-teal-400 dark:hover:border-teal-500"
        }`}
      >
        {/* i18n-ignore: the "i" glyph, not copy */}i
      </button>
      {open && (
        <div
          data-testid={`${testid}-popover`}
          className={`absolute z-50 top-full mt-1 w-80 max-w-[80vw] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg p-3 text-xs text-slate-700 dark:text-slate-200 leading-snug ${
            align === "right" ? "right-0" : "left-0"
          }`}
          // Don't bubble clicks inside the popover up to the icon
          // button's onClick (which would toggle it back off).
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </span>
  );
};

export default InfoCircle;
